// @flow

import config from 'config/app.config';
import { RedirectServices, getRedirectUrl } from 'config/paths';
import { useCallback } from 'react';
import styled from 'styled-components';
import { Licenses } from 'types/common';

// FR
import advertisementImage from '../../../assets/images/avertissement.png';
// ES
import dswvImage from '../../../assets/images/de/dswv.png';
import gglImage from '../../../assets/images/de/ggl.png';
import under18EsImage from '../../../assets/images/es/18.png';
import autoProhibitionImage from '../../../assets/images/es/autoprohibition.png';
import juegoSeguroImage from '../../../assets/images/es/jugarsegurokk.png';
// DE
import exclusaoImage from '../../../assets/images/pt/exclusao.png';
import sicadImage from '../../../assets/images/pt/sicad.png';
import under18Image from '../../../assets/images/under18.png';
import { __ } from '../../../i18n/l10n';
import { media } from '../../../styles/GlobalStyle';
// PT

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- Used in JSX below
import { JuegoConResponsabilidad, NoHayJuego, SejaResponsavel } from './common';
import { ForbiddenLogo, ResponsiblyLogo } from './FooterSpritesheet';

const Container = styled.div`
  ${media.greaterThan('desktop')`
    display: none;
  `}
  margin-bottom: 1em;
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- Used in JSX below
const Column = styled(Container)`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- Used in JSX below
const Wrap = styled(Container)`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-evenly;

  width: 80%;
  align-self: center;
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- Used in JSX below
const Advertisement = styled.div`
  margin-bottom: 1em;
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- Used in JSX below
const Forbidden = styled.div`
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- Used in JSX below
const ForbiddenText = styled.div`
  margin-left: 1em;
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- Used in JSX below
const Img40 = styled.img`
  height: 40px;
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- Used in JSX below
const Img30 = styled.img`
  height: 30px;
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- Used in JSX below
const TextContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0.5em 1em;
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- Used in JSX below
const MargedLogo = styled.div`
  margin-right: 0.7em;
  min-width: 34px;
  transform: scale(0.8);
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- Used in JSX below
const InlineLogoText = styled.div`
  font-size: 0.9em;
  display: flex;
  align-items: center;
  align-self: flex-start;

  cursor: ${({ onClick }) => (onClick ? 'pointer' : 'auto')};
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- Used in JSX below
const ImageSizeDE = styled.img`
  height: 25px;
  margin-top: 3px;

  & + & {
    margin-left: 25px;
  }
`;

type FooterProps = {
  className?: string,
};

export const Footer = ({ className }: FooterProps) => {
  const redirectToResponsibleGaming = useCallback(() => {
    window.location.href = getRedirectUrl(RedirectServices.RESPONSIBLE_GAMING);
  }, []);

  switch (config.license) {
    case Licenses.ES:
      return (
        <>
          <Wrap className={className}>
            <TextContainer>
              <JuegoConResponsabilidad />
            </TextContainer>
            <TextContainer>
              <NoHayJuego />
            </TextContainer>
            <a href={'https://www.dev.winamax.es/respecto-a-winamax_proteccion-de-los-jugadores_juego-responsable'}>
              <Img30 src={under18EsImage} />
            </a>
            <a
              href={'http://www.ordenacionjuego.es/es/rgiaj'}
              target={'_blank'}
              rel={'noopener noreferrer'}
            >
              <Img40 src={autoProhibitionImage} />
            </a>
            <a
              href={'https://www.ordenacionjuego.es/participantes-juego/juego-seguro'}
              target={'_blank'}
              rel={'noopener noreferrer'}
            >
              <Img30 src={juegoSeguroImage} />
            </a>
          </Wrap>
        </>
      );
    case Licenses.PT:
      return (
        <>
          <Wrap className={className}>
            <TextContainer>
              <SejaResponsavel />
            </TextContainer>
            <a href={'/a-winamax_protecao-dos-jogadores_jogo-responsavel'}>
              <Img30 src={under18EsImage} />
            </a>
            <a
              href={'https://www.srij.turismodeportugal.pt/pt/jogo-responsavel/autoexclusao-e-proibicao/'}
              target={'_blank'}
              rel={'noopener noreferrer'}
            >
              <Img30 src={exclusaoImage} />
            </a>
            <a
              href={'https://www.sicad.pt/pt/Paginas/default.aspx'}
              target={'_blank'}
              rel={'noopener noreferrer'}
            >
              <Img30 src={sicadImage} />
            </a>
          </Wrap>
        </>
      );
    case Licenses.DE:
      return (
        <>
          <Column className={className}>
            <InlineLogoText onClick={redirectToResponsibleGaming}>
              <MargedLogo as={ResponsiblyLogo} />
              {__('Responsible gaming')}
            </InlineLogoText>
            <InlineLogoText>
              <MargedLogo as={ForbiddenLogo} />
              {__('No minors')}
            </InlineLogoText>
            <InlineLogoText>
              <ImageSizeDE src={dswvImage} />
              <ImageSizeDE src={gglImage} />
            </InlineLogoText>
          </Column>
        </>
      );
    case Licenses.FR:
      return (
        <>
          <Column className={className}>
            <Forbidden>
              <Img40
                src={under18Image}
                alt={'Interdit aux mineurs'}
              />
              <ForbiddenText>
                {'Les jeux d’argent et de hasard'}
                <br />
                {'sont interdits aux mineurs'}
              </ForbiddenText>
            </Forbidden>
            <Advertisement>
              <img
                src={advertisementImage}
                alt={'Jouer comporte des risques'}
                width={'100%'}
              />
            </Advertisement>
          </Column>
        </>
      );
    default:
      return null;
  }
};
